import { PlayerMarketListing } from '@components/arsenal/playerMarket/PlayerMarketListing';
import { MarketCredits } from '@components/arsenal/market/MarketCredits';
import { ArsenalHeader } from '@components/arsenal/ArsenalHeader';
import { ArsenalBack } from '@components/arsenal/ArsenalBack';
import { useHistoryInterceptor } from '@helpers/history';
import { IconUsers } from '@tabler/icons-react';
import { useTradingListingsQuery } from '@http';
import { useTranslation } from 'react-i18next';
import { Select } from '@components/ui/Select';
import * as Shared from '@shared/lib';
import { useAppStore } from '@stores';
import { useState } from 'react';

export const disabledMarketItems = [
  Shared.ItemCode.HEALTH,
  Shared.ItemCode.CREDIT,
  Shared.ItemCode.ENERGY
];

export function PlayerMarket() {
  const [showItem, setShowItem] = useState('');
  const [tab, setTab] = useState('buy');

  const { t } = useTranslation(undefined, { keyPrefix: 'PlayerMarket' });
  const { t: tItems } = useTranslation(undefined, { keyPrefix: 'Items' });
  const { data: listings } = useTradingListingsQuery();
  const { id } = useAppStore();

  useHistoryInterceptor(() => {
    if (showItem) { setShowItem(''); return true; }
    return false;
  }, { disabled: !showItem });

  const items = Shared.dropData
    .filter(i => !disabledMarketItems.includes(i.id))
    .filter(i => !('crafting' in i));

  const myListings = listings?.filter(l => l.user.id === id) ?? [];
  const showListings = showItem
    ? listings?.filter(l => l.key === showItem) ?? []
    : [];

  const tabs = [
    { value: 'buy', label: t('buy') },
    { value: 'myListings', label: t('myListings') },
  ];

  const getPrice = (key: string) => {
    const filtered = listings?.filter(l => l.key === key);
    return filtered?.sort((a, b) => a.price - b.price)[0]?.price ?? 0;
  };

  const getListingCount = (key: string) => {
    return listings?.filter(l => l.key === key).length ?? 0;
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <ArsenalHeader description={t('description')} title={t('title')} bg="market.avif" />
      <MarketCredits />
      <Select onChange={setTab} options={tabs} value={tab} />

      {tab === 'myListings' && myListings.map(l => (
        <PlayerMarketListing listing={l} key={l.id} />
      ))}

      {tab === 'myListings' && !myListings.length && (
        <p className="mt-6 text-center text-sm text-gray-500">
          {t('noListings')}
        </p>
      )}

      {tab === 'buy' && showItem && (
        <>
          <ArsenalBack onClick={() => setShowItem('')} bg="market.avif" />

          {showListings.length > 0 && showListings.map(l => (
            <PlayerMarketListing listing={l} key={l.id} />
          ))}

          {!showListings.length && (
            <p className="mt-6 text-center text-sm text-gray-500">
              {t('noListingsGeneral')}
            </p>
          )}
        </>
      )}

      {tab === 'buy' && !showItem && (
        <div className="flex-1 overflow-y-auto">
          {items.map((item) => (
            <button 
              className="bg-black flex border-b border-secondary w-full text-left"
              onClick={() => setShowItem(item.key)}
              key={item.id}
            >
              <div className="w-16 h-16 p-4 relative border-r border-secondary">
                <div className="absolute inset-0 grid-overlay" />
                <img
                  className="w-full h-full object-contain"
                  src={`/assets/items/${item.key}.avif`}
                  alt={item.key}
                />
              </div>

              <div className="flex-1 flex flex-col justify-center ml-2">
                <p>{tItems(item.key)}</p>
                <div className="flex w-full mt-2">
                  <div className="flex-1 flex items-center">
                    <img src="/assets/items/credit.avif" className="h-4 mr-1.5" alt="credit" />
                    <p className="text-mono text-sm text-gold leading-none">{getPrice(item.key)}</p>
                  </div>
                  <div className="flex-1 flex items-center text-gray-400">
                    <IconUsers className="h-4 mr-1" />
                    <p className="text-mono text-sm leading-none">{getListingCount(item.key)}</p>
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}